import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { haalKwaliteit } from '../../services/professionalDataApi';
import { Kaart, KpiTegel, Laden } from '../data/DataFilterBalk';
import { Legenda, REEKS_KLEUREN } from '../data/DataCharts';
import { HorizontaleBalken, RadarGrafiek } from './Grafieken';
import { Fout, Grendel, getal } from './Bediening';

const LANDELIJK_KLEUR = '#0B1F3A';
const score = (v: number | null) => (v == null ? '—' : `${getal(v)} / 5.0`);

export const KwaliteitOnderdeel: React.FC = () => {
  const { t } = useTranslation('professionalData');
  const q = useQuery({ queryKey: ['professional-data', 'kwaliteit'], queryFn: haalKwaliteit, staleTime: 60_000 });
  const d = q.data;
  if (q.isLoading) return <Laden />;
  if (q.isError) return <Fout fout={q.error} />;
  if (!d) return null;
  const min = d.minimum_reviews;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <KpiTegel
          label={t('kwaliteit.score')}
          waarde={score(d.eigen.score)}
          sub={d.eigen_onderdrukt ? t('kwaliteit.onderdruktEigen', { min }) : `${t('kwaliteit.scoreDesc')} · ${t('kwaliteit.reviews', { count: d.eigen.aantal ?? 0 })}`}
        />
        <KpiTegel
          label={t('kwaliteit.landelijk')}
          waarde={score(d.landelijk.score)}
          sub={d.landelijk.score == null ? t('kwaliteit.onderdruktLandelijk') : t('kwaliteit.landelijkDesc')}
        />
      </div>

      <Kaart titel={t('kwaliteit.perOpdrachtgeverTitel')} uitleg={t('kwaliteit.perOpdrachtgeverUitleg', { min })}>
        {d.opdrachtgevers.length === 0 ? (
          <p className="text-sm text-gray-500">{t('geenData')}</p>
        ) : (
          <>
            <HorizontaleBalken
              rijen={d.opdrachtgevers.map((o) => ({
                key: o.employer_id,
                label: o.naam,
                waarde: o.score,
                sub: o.aantal != null ? t('kwaliteit.reviews', { count: o.aantal }) : undefined,
              }))}
              max={5}
              formaat={(v) => getal(v)}
              referentie={d.landelijk.score != null ? { waarde: d.landelijk.score, label: t('kwaliteit.landelijk') } : null}
              onderdruktLabel={t('kwaliteit.placeholder', { min })}
            />
            {d.opdrachtgevers.some((o) => o.onderdrukt) && (
              <div className="mt-4">
                <Grendel>{t('kwaliteit.placeholderLegenda', { min })}</Grendel>
              </div>
            )}
          </>
        )}
      </Kaart>

      <Kaart titel={t('kwaliteit.radarTitel')} uitleg={t('kwaliteit.radarUitleg')}>
        <div className="grid items-center gap-6 lg:grid-cols-2">
          <div>
            <div className="mb-2 flex justify-center">
              <Legenda
                items={[
                  { label: t('kwaliteit.jij'), kleur: REEKS_KLEUREN[0] },
                  { label: t('kwaliteit.landelijk'), kleur: LANDELIJK_KLEUR },
                ]}
              />
            </div>
            <RadarGrafiek
              assen={d.dimensies.map((x) => ({ key: x.key, label: t(`kwaliteit.dimKort.${x.key}`) }))}
              reeksen={[
                { key: 'eigen', label: t('kwaliteit.jij'), kleur: REEKS_KLEUREN[0], waarden: d.eigen.dimensies },
                { key: 'landelijk', label: t('kwaliteit.landelijk'), kleur: LANDELIJK_KLEUR, waarden: d.landelijk.dimensies, gestippeld: true },
              ]}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                  <th className="py-2 pr-3 font-medium">{t('kwaliteit.dimensie')}</th>
                  <th className="py-2 pr-3 text-right font-medium">{t('kwaliteit.jij')}</th>
                  <th className="py-2 text-right font-medium">{t('kwaliteit.landelijk')}</th>
                </tr>
              </thead>
              <tbody>
                {d.dimensies.map((x, i) => (
                  <tr key={x.key} className="border-b border-gray-100 align-top">
                    <td className="py-2 pr-3">
                      <span className="font-medium text-gray-900">{i + 1}. {t(`kwaliteit.dim.${x.key}`)}</span>
                      <span className="block text-xs text-gray-500">{t(`kwaliteit.dimBron.${x.key}`)}</span>
                    </td>
                    <td className="py-2 pr-3 text-right font-semibold">{d.eigen.dimensies[x.key] == null ? <span className="text-xs font-normal text-gray-400">{t('kwaliteit.nogGeen')}</span> : getal(d.eigen.dimensies[x.key]!)}</td>
                    <td className="py-2 text-right">{d.landelijk.dimensies[x.key] == null ? <span className="text-xs text-gray-400">{t('kwaliteit.nogGeen')}</span> : getal(d.landelijk.dimensies[x.key]!)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-gray-500">{t('kwaliteit.dimensieNoot')}</p>
          </div>
        </div>
      </Kaart>
    </div>
  );
};
